import { useState, useEffect, useRef } from "react";
import { HEALTH_INFORMATION } from "../../constants/constant";

const HealthInformationCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    resetTimeout();
    timeoutRef.current = setTimeout(() => {
      handleNext();
    }, 6000);

    return () => {
      resetTimeout();
    };
  }, [currentIndex]);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === HEALTH_INFORMATION.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? HEALTH_INFORMATION.length - 1 : prevIndex - 1,
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    }

    if (isRightSwipe) {
      handlePrev();
    }

    setTouchStart(0);
    setTouchEnd(0);
    setIsDragging(false);
  };

  const handleMouseDown = (e) => {
    setTouchStart(e.clientX);
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setTouchEnd(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    }

    if (isRightSwipe) {
      handlePrev();
    }

    setTouchStart(0);
    setTouchEnd(0);
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp();
    }
  };

  return (
    <div className="w-full mx-auto relative">
      <div
        className="w-full overflow-hidden bg-blue-50 border border-slate-200 rounded-2xl"
        style={{ aspectRatio: "3/1" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="flex h-full transition-transform duration-500 ease-out"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            cursor: isDragging ? "grabbing" : "grab",
          }}
        >
          {HEALTH_INFORMATION.map((info, index) => (
            <div
              key={index}
              className="min-w-full h-full flex items-center justify-center px-3"
              style={{ userSelect: "none" }}
            >
              <div className="flex items-start gap-2 w-full max-w-full text-slate-500">
                <div className="w-8 h-8 flex-shrink-0">
                  <img
                    src={info.icon}
                    alt={info.title}
                    className="w-full h-full object-contain"
                    draggable="false"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="font-semibold text-base md:text-lg mb-1 line-clamp-2">
                    {info.title}
                  </h1>
                  <p className="text-md md:text-sm line-clamp-3">
                    {info.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="absolute -bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
          {HEALTH_INFORMATION.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                currentIndex === index
                  ? "bg-primary w-3"
                  : "bg-slate-300 hover:bg-slate-400"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HealthInformationCarousel;
